/**
 * Co gra ma do zagrania.
 *
 * A file rather than a list in the code, for the reason `catalogue.json` and
 * `materials.json` are files: these are things somebody adds in an evening. A
 * sixth footstep variant or the sound of a river is a line here and a file
 * beside it — never an edit to TypeScript.
 *
 * **Ambience is keyed by the dictionaries the rest of the game already reads.**
 * `OBJ_FOREST` and `BIO_OCEAN` mean here exactly what they mean to the rules
 * that decide where a lumberjack may stand, because the same `objects.json` and
 * `biomes.json` answer both. A second definition of "what counts as a wood",
 * living in a sound module, is the twin-rule debt this project already carries
 * once for movement and does not need to carry again.
 *
 * **Provenance rides with the entry.** Where a file came from, who made it and
 * under what licence is the one thing nobody reconstructs from memory a year
 * later, and keeping it beside the filename is what stops the two drifting
 * apart. Even CC0 wants an answer to "where is this from".
 */

/** The shape this file knows how to read. */
export const AUDIO_VERSION = 1;

/** Where a sound came from, so the question can be answered later. */
export interface Provenance {
  source?: string;
  author?: string;
  license?: string;
}

export interface MusicTrack extends Provenance {
  file: string;
  /** Trim for this track alone, where levelling on disk was not enough. */
  gain?: number;
}

export interface AmbienceBed extends Provenance {
  /**
   * What has to be near for this to be heard: `OBJ_*` or `BIO_*`.
   *
   * Read through the object and biome dictionaries, so a key that no dictionary
   * knows is simply never near anything — the same silence as a wood that does
   * not grow here.
   */
  key: string;
  file: string;
  /** How many rings of cells around the player count towards how loud it is. */
  radius: number;
  gain?: number;
}

export interface StepSound extends Provenance {
  file: string;
}

/** One stroke of an axe — the same shape as a footstep, and played the same way. */
export type ChopSound = StepSound;

/**
 * Odgłos placu budowy: kilka nagrań grających na zmianę, w kółko.
 *
 * Nie jest wpisem `ambience`, bo tamte klucze czytają słowniki terenu, a plac
 * budowy jest stanem gry — głośność bierze się z odległości do najbliższego
 * placu, a nie z udziału pól.
 */
export interface ConstructionSound {
  files: StepSound[];
  /** Z ilu pól jeszcze słychać plac. */
  radius: number;
  gain?: number;
}

export interface AudioManifest {
  version: number;
  music: MusicTrack[];
  ambience: AmbienceBed[];
  steps: StepSound[];
  /**
   * Uderzenia topora przy wycince. Opcjonalne jak każda lista: manifest bez
   * nich jest dalej manifestem w wersji 1, a wycinka jest wtedy cicha.
   */
  chops: ChopSound[];
  /** Opcjonalne: bez tego wpisu budowa jest cicha. */
  construction?: ConstructionSound;
}

/**
 * Read it, or decide there is no sound.
 *
 * **Never throws, and that is deliberate.** Sound is the last thing that may
 * take anything else down with it: a missing file, a manifest in a version this
 * build does not read, a server that has not been redeployed — all of them end
 * as a quiet game and a line in the console. This client has already learned
 * that lesson the expensive way, when a catalogue one version ahead took the
 * whole HUD with it because it shared a promise with everything else.
 */
export async function loadAudioManifest(base = '/audio'): Promise<AudioManifest | null> {
  try {
    const response = await fetch(`${base}/audio.json`);
    if (!response.ok) {
      console.info(`dźwięk: nie ma manifestu (${response.status}) — gra będzie cicha`);
      return null;
    }

    const manifest = (await response.json()) as AudioManifest;
    if (manifest.version !== AUDIO_VERSION) {
      console.warn(
        `dźwięk: manifest w wersji ${manifest.version}, klient czyta ${AUDIO_VERSION} — gra będzie cicha`
      );
      return null;
    }

    // Absent lists mean none, so a manifest carrying only music is a legal
    // manifest rather than one that needs three empty arrays written out.
    return {
      version: manifest.version,
      music: manifest.music ?? [],
      ambience: manifest.ambience ?? [],
      steps: manifest.steps ?? [],
      chops: manifest.chops ?? [],
      construction: manifest.construction,
    };
  } catch (error) {
    console.info('dźwięk: manifest nie wczytał się — gra będzie cicha', error);
    return null;
  }
}
